<template>
  <div class="desktopwrapper">
    <slot name="header"></slot>
    <div class="layout" ref="layout">
      <Sidebar 
        :style="{'height': sidebarHeight}"
        @date-change="dateChangedOnSideBarPicker"
        @option-chosen="userChooseOption" />
      <div class="content" 
        :style="getContentStyles"
        :class="{'filterOpen': openFilter}">
        <OptionSelector 
          v-if="!showResultsView" 
          @setOption="userChooseOption" 
          :options="options" />
        <FilterWindow
          @close-window="closeWindow"
          :backtext="backtext"
          :openWindow="openFilter" >
            <Forms 
              v-if="selectedOption != null"
              @submit-form-data="submitForm"
              :openFilter="openFilter"
              :selectedOption="selectedOption" />
        </FilterWindow>
        <div class="resultviewer"
          v-if="showResultsView">
            <div class="maintoolbar">
              <DatesNavigation
                class="dnavigation"
                @set-week="userChangeDatesOnNavigation"
                :monthNavigation="selectedOption?.id == 3"
                :currentweek="selectedWeek" />
              <TooltipManager>
                <Tooltip :text="$t('addcalendar')">  
                  <a class="btnaction"
                    href="https://helpdesk.uma.pt/cloud365/office365/adicionar-horarios/"
                    target="_blank" >
                    <img src="@/assets/images/calendar.png" />
                  </a>
                </Tooltip>
                <Tooltip :text="$t('imprimirhorario')"> 
                  <a class="btnaction" @click="printHorarios">
                    <img class="printer" src="@/assets/images/printer.png" />
                  </a> 
                </Tooltip> 
              </TooltipManager>
            </div>
            <FilterViewer
              @remove-filter="removeFilter"
              @editFilters="openWindow"
              :fields="searchedFormValues" />  
            <ResultsDisplayer 
              :selectedWeek="selectedWeek"
              :searchedFilter="searchedFilter" />  
        </div>
      </div>
    </div>
  </div>
  <Printview
    v-if="searchedFormValues != null"
    class="printview"
    :fields="searchedFormValues"
    :isAvaliacao="searchedFilter?.value == 'avaliacao'" />
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import FilterWindow from '@/components/filterwindow/filterwindow.vue';
import FilterForm from '@/containers/desktop/FilterForm.vue';
import DatesNavigation from './desktop/datesNavigation.vue';
import OptionSelector from '@/components/optionSelector.vue';
import FilterViewer from '@/components/filterviewer/filtersviewer.vue';
import Forms from '@/containers/desktop/Forms.vue';
import ResultsDisplayer from '@/containers/desktop/resultsDisplayer.vue'; 
import Printview from '@/containers/desktop/printview.vue'; 
import Sidebar from '@/containers/desktop/Sidebar.vue';
import { i18n } from '@/main.js';
import { useMenuStore } from "@/stores/menu.ts";
import { useHorarios } from "@/stores/horarios.ts";
import { useEventSearch } from "@/stores/eventSearch.ts";
import { Tooltip, TooltipManager } from "umaui";


export default {
  name: 'DesktopView',
  props:[
    "selectedOption",
    "searchedFilter",
    "headerHeight"
  ],
  components: {
    TooltipManager,
    Tooltip,
    FilterWindow,
    FilterForm,
    DatesNavigation,
    OptionSelector,
    FilterViewer,
    Forms,
    ResultsDisplayer,
    Sidebar,
    Printview
  },
  setup(props){

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0"); // add leading zeros
      const day = date.getDate().toString().padStart(2, "0"); // add leading zeros
      return `${year}-${month}-${day}`;
    };

    const eventsearch = useEventSearch();
    const selectedWeek = computed(()=> eventsearch.selectedWeek);
    const currentFilters = computed(()=>eventsearch.currentFilters);

    function removeFilterVal(propertyName){
      eventsearch.removeFilterValue(propertyName);
    }
    
    function updateSelectedWeek(selectedWeek){
      eventsearch.setSelectedWeek(selectedWeek);
    }

    function updateSearchFilter(filter) {
      eventsearch.updateSearchFilter(filter);
    }

    function addParametersToUrl(updatedCurrentFilters){
      let params = "";
      let addedDate = false;
      for (const key in updatedCurrentFilters) {
        if(key != "date" && key != "selectedWeek"){
          if(updatedCurrentFilters[key]?.value != null){
            params = `${params}&${key}=${updatedCurrentFilters[key].value}`;
          }
        }else{
          if(!addedDate){
            const start = formatDate(updatedCurrentFilters[key].start);
            const end = formatDate(updatedCurrentFilters[key].end);
            params = `${params}&start=${start}&end=${end}`;
            addedDate = true;
          }
        };
      }
      const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${params}`;
      window.history.pushState({ path: newurl }, '', newurl);
    }

    function updateCurrentFilters(updatedCurrentFilters){
      eventsearch.updateCurrentFilters(updatedCurrentFilters);
      addParametersToUrl(updatedCurrentFilters);
    }

    const horariosStore = useHorarios();
    const showHeader = computed(() => horariosStore.headerVisible);
    const isUpdatingHorario = computed(()=>horariosStore.isUpdatingHorario)
    function fetchHorarios(){
      horariosStore.fetchHorario();
    } 

    //#region FilterViewer Data
    const searchedFormValues = computed(()=> eventsearch.searchedFormValues);

    function setSearchedFormValues(submittedFormFields){
      eventsearch.setSearchedFormValues(submittedFormFields);
    }

    function setFieldOnFormValues(key,val){
      eventsearch.setFieldOnFormValues(key,val);
    }
    //#endregion

    const menuStore = useMenuStore();
    const options = computed(() => menuStore.menuItems);
    function handleSelect(id) {
      menuStore.selectMenuItem(id);
    }

    const locale = computed(() => i18n.global.locale); 

    watch(locale, newval => {
      menuStore.selectMenuItem(null);
      eventsearch.updateCurrentFilters(null);
      eventsearch.updateSearchFilter(null);
      openFilter.value = false;
    });

    const openFilter = ref(false);

    const layout = ref(null);
    const headerHeight = ref(0)

    const getYPosition = (element) => {
      var rect = element.getBoundingClientRect();
      return rect.top + window.scrollY;
    }

    const updateHeaderHeight = () => {
      headerHeight.value = getYPosition(layout.value);
    }
    
    onMounted(() => {
      window.addEventListener("resize",updateHeaderHeight)
      updateHeaderHeight()
    })

    onUnmounted(() => {
      window.removeEventListener("resize",updateHeaderHeight)
    })   

    const getContentStyles = computed(()=>{
      return {
        "height": `calc(100vh-${headerHeight.value + 20}px)`
      }
    })

    const sidebarHeight = computed(() => {
      return `calc(100vh - ${headerHeight.value}px`;
    })

    //#region PRINT 
    const printMode = ref(false);

    const printHorarios = () => {
      printMode.value = !printMode.value;
      const mediaQueryList = window.matchMedia("print");
      const listener = (e) => {
        if (!e.matches) {
          printMode.value = false;
          mediaQueryList.removeEventListener('change', listener); 
        }
      };
      mediaQueryList.addEventListener('change', listener);
      nextTick(() => {
        window.print();
      });
    };

    onMounted(() => {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          printMode.value = false;
        }
      });
    });

    onUnmounted(() => {
      document.removeEventListener("visibilitychange", () => {
        printMode.value = false;
      });
    });

    //#endregion

    return {
      printMode,
      printHorarios,
      showHeader,
      setSearchedFormValues,
      setFieldOnFormValues,
      searchedFormValues,
      locale,
      options,
      selectedWeek,
      removeFilterVal,
      updateSelectedWeek,
      currentFilters,
      handleSelect,
      openFilter,
      fetchHorarios,
      isUpdatingHorario,
      updateSearchFilter,
      updateCurrentFilters,
      getContentStyles,
      layout,
      sidebarHeight
    }
  },
  computed:{
    showResultsView(){
      return this.searchedFilter != null
    },
    backtext(){
      if(this.selectedOption != null){
        return this.$t(this.selectedOption.value);
      }
      return "";
    }
  },
  methods:{
    userChangeDatesOnNavigation(date){
      this.updateSelectedWeek(date);
      this.updateCurrentFilters({
        ...this.currentFilters,
        date: date,
        selectedWeek: date
      })
      this.fetchHorarios();
    },
    dateChangedOnSideBarPicker(data){
      this.updateSelectedWeek(data);
      if(this.searchedFilter != null && !this.openFilter){
        this.fetchHorarios();
      }
    },
    userChooseOption(option){
      this.openWindow();
      this.handleSelect(option.id);
    },
    openWindow(){
      this.openFilter = true;
    },
    closeWindow(){
      this.openFilter = false;
      if(this.searchedFilter != null){
        this.handleSelect(this.searchedFilter.id);  
      }
      if(this.currentFilters != null && Object.keys(this.currentFilters).length > 0){
        this.updateSelectedWeek(this.currentFilters.selectedWeek);
      }
    },
    removeFilter(option){
      this.setFieldOnFormValues(option,null);
      this.removeFilterVal(option);
      this.fetchHorarios();
    },
    submitForm(data){
      this.openFilter = false;
      const [ selectedDates, params, form ] = data;
      this.setSearchedFormValues(form);
      this.updateSearchFilter(this.selectedOption);
      this.updateCurrentFilters({
        ciclo: params?.ciclo,
        ano: params?.ano,
        curso: params?.curso,
        cursoid: params?.curso?.valor,
        disciplina: params?.disciplina,
        turma: params?.turma,
        selectedWeek: selectedDates,
        sala: params?.sala,
        docente: params?.docente,
        avaliacao: params?.avaliacao,
        date: selectedDates,
        selectedOption:this.selectedOption
      });
      this.fetchHorarios();
    }
  }
}
</script>

<style scoped>
.dnavigation{
  margin-right: 30px;
}

.btnaction{
  border-radius: 50%;
  font-size: 20px;
  width: 40.5px;
  height: 40.5px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-content: center;
  cursor: pointer;
}
.btnaction:first-of-type{
  margin-right: 10px;
}
.btnaction:hover{
  background-color: #f5f3f3;
}

.btnaction img{
  width: 35px;
  height: 35px;
}

.btnaction img.printer{
  width: 35px !important;
  height: 33px !important;
}

.maintoolbar{
  display: flex;
  flex: 1;
  flex-direction: row;
  margin-bottom: 25px;
}

.maintoolbar i{
  color: #1976d2;
  font-size: 23px;
  /*margin-left: 20px;*/
  cursor: pointer;
  /*margin-left: 40px;*/
}


.content.filterOpen{
  overflow-y: hidden;
}

.layout .content{
  padding: 20px;
  padding-bottom: 0; 
  position: relative;
  overflow-y: hidden;
}

.layout{
  display: flex;
}

.sidebar {
  flex-shrink: 0;
}

.content{
  flex-grow: 1;
}

.layout .sidebar.open{
  width: 50%;
}

.layout .content {
  padding-right: 9%;
  padding-left: 40px;
}

@media only screen and (min-width: 1351px) and (min-width:1650px) {
  .layout .content {
    padding-right: 5%;
  }
}


@media only screen and (max-width:1350px) {
  .content.filterOpen{
    overflow-y: hidden;
  }
}

@media screen and (max-width:1024px) {
  .sbf{
    display: none;
  }

  .ndf{
    display: block;
    padding-left: 0;
  }  

  .sidebar{
    -webkit-box-flex: 0;
    -ms-flex: 0 0 8.33333%;
    flex: 0 0 8.33333%;
    max-width: 8.33333%;
  }
  .content{
    -webkit-box-flex: 0;
    -ms-flex: 0 0 91.66666%;
    flex: 0 0 91.66666%;
    max-width: 91.66666%;
  }
}


@media only screen and (max-width:1350px) and (min-width: 768px) {

  .layout .content {
    padding-left: 30px;
    padding-right: 65px;
  }

  .gridwrapper {
    padding-top: 8px;
  }

}

@media only screen and (max-width:1350px) and (min-width: 1024px) {
  .layout .content {
    padding-left: 30px;
    padding-right: 20px;
  }
}

@media only screen and (max-width:1050px) and (min-width: 768px) {

  .layout .content {
    padding-right: 50px;
    padding-left: 20px;
  }

}

@media only screen and (max-width:900px) and (min-width: 768px) {

  .layout .content {
    padding-right: 30px;
    padding-left: 20px;
  }

  .gridwrapper {
    grid-column-gap: 10px;
  }
}

@media screen{
  .printview{
    visibility: hidden;
  }
}

@media print{
  .desktopwrapper{
    display: none;
  }

  .printview{
    visibility: visible;
  }
}

</style>


